<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Vendas de Produtos</h5>

      <BButton
        v-if="hasPermission('adicionar_vendas')"
        variant="primary"
        class="d-flex align-items-center"
        @click="irParaCadastro"
      >
        <i class="bi bi-plus-circle-fill" title="Nova Venda"></i>
      </BButton>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap: 10px;">
      <select
        v-model="filters.quando"
        class="form-control same-height"
        @change="filtrar"
      >
        <option value="Todos">Data: Todos</option>
        <option value="Hoje">Hoje</option>
        <option value="Anteriores">Anteriores</option>
      </select>

      <input
        v-model="filters.cliente"
        @input="filtrar"
        type="text"
        class="form-control same-height"
        placeholder="Filtrar por Cliente"
      />

      <select
        v-model="filters.status"
        class="form-control same-height"
        @change="filtrar"
      >
        <option value="Todos">Status: Todos</option>
        <option value="Pendentes">Pendentes</option>
        <option value="Finalizadas">Finalizadas</option>
      </select>

      <b-button
        variant="secondary"
        @click="limparFiltros"
        class="d-flex align-items-center"
      >
        <i class="bi bi-funnel" title="Limpar Filtros"></i>
      </b-button>
    </div>

    <!-- Tabela nativa com ordenação -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="toggleSort('id')">
              # {{ arrow('id') }}
            </button>
          </th>

          <th>
            <button class="btn btn-link p-0" @click="toggleSort('cliente_nome')">
              Cliente {{ arrow('cliente_nome') }}
            </button>
          </th>

          <th>
            <button class="btn btn-link p-0" @click="toggleSort('data_venda')">
              Data Venda {{ arrow('data_venda') }}
            </button>
          </th>

          <th class="text-center">
            <button class="btn btn-link p-0" @click="toggleSort('valor_total')">
              Valor Total {{ arrow('valor_total') }}
            </button>
          </th>

          <th>
            <button class="btn btn-link p-0" @click="toggleSort('pendente')">
              Status {{ arrow('pendente') }}
            </button>
          </th>

          <th class="text-end">Ações</th>
        </tr>
      </thead>

      <tbody>
        <tr v-for="item in displayItems" :key="item.id">
          <td>{{ item.id }}</td>
          <td>{{ item.cliente_nome }}</td>
          <td>{{ formatDate(item.data_venda) }}</td>
          <td class="text-center">R$ {{ formatDecimal(item.valor_total) }}</td>

          <td>
            <span :class="item.pendente ? 'text-danger' : 'text-success'">
              {{ item.pendente ? 'Pendente' : 'Finalizada' }}
            </span>
          </td>

          <td class="text-end">
            <i
              v-if="hasPermission('detalhar_vendas')"
              class="bi bi-eye-fill text-primary me-2"
              title="Detalhar"
              @click="detalhar(item.id)"
            />

            <i
              v-if="hasPermission('alterar_vendas')"
              class="bi bi-pencil-fill text-warning me-2"
              title="Editar"
              @click="editar(item.id)"
            />

            <i
              v-if="hasPermission('excluir_vendas')"
              class="bi bi-trash-fill text-danger me-2"
              title="Excluir"
              @click="excluir(item.id)"
            />

            <i
              class="bi bi-printer-fill me-2"
              :class="{
                'text-success': !item.pendente && hasReceiptPermission('emitir_recibo_vendas'),
                'text-muted': item.pendente || !hasReceiptPermission('emitir_recibo_vendas'),
                'cursor-pointer': !item.pendente && hasReceiptPermission('emitir_recibo_vendas'),
                'cursor-not-allowed': item.pendente || !hasReceiptPermission('emitir_recibo_vendas')
              }"
              :title="!item.pendente && hasReceiptPermission('emitir_recibo_vendas') ? 'Imprimir Recibo' : 'Recibo Não Disponível'"
              @click="!item.pendente && hasReceiptPermission('emitir_recibo_vendas') && imprimir(item.id)"
            />
          </td>
        </tr>

        <tr v-if="displayItems.length === 0">
          <td colspan="6" class="text-center text-muted py-3">
            Nenhuma venda encontrada.
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>

      <div class="d-flex align-items-center" style="gap: .5rem;">
        <b-form-select
          v-model="limit"
          :options="pageOptions.map(n => ({ value: n, text: n + ' / página' }))"
          size="sm"
          style="width: 6rem"
          @update:model-value="onChangeLimit"
        />
      </div>

      <div class="d-flex align-items-center">
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
          @update:model-value="onPageChange"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useSortableTable } from '@/utils/useSortableTable';
import { useReceitasPermissions } from '@/composables/useReceitasPermissions';
import { usePermissions } from '@/composables/usePermissions';
import { BButton, BFormSelect, BPagination } from 'bootstrap-vue-next';
import axios from '@/services/axios';
import globalUtils from '@/utils/globalUtils';

const router = useRouter();
const { fetchPermissions, hasPermission } = usePermissions();
const { fetchPermissions: fetchReceiptPermissions, hasPermission: hasReceiptPermission, authorizeReceiptAction } = useReceitasPermissions();

const {
  totalItems,
  page,
  limit,
  filters,
  sort,
  arrow,
  load: loadRawVendas,
  items: rawItems
} = useSortableTable('/loja/vendas', { key: 'data_venda', order: 'asc' });

const sortKey = ref('data_venda');

const items = ref([]);
const displayItems = ref([]);

Object.assign(filters.value, {
  quando: 'Todos',
  cliente: '',
  status: 'Todos'
});

const pageOptions = [10, 15, 20, 25, 50];

const startRow = computed(() => {
  if (!totalItems.value || displayItems.value.length === 0) return 0;
  return (page.value - 1) * limit.value + 1;
});

const endRow = computed(() => {
  if (!totalItems.value || displayItems.value.length === 0) return 0;
  return Math.min(page.value * limit.value, totalItems.value);
});

function formatDecimal(val) {
  const num = Number(val);
  return isNaN(num)
    ? ''
    : num.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function formatDate(dt) {
  const formatted = globalUtils.formatarData(dt);
  return formatted || '—';
}

function irParaCadastro() {
  router.push('/loja/vendas/novo');
}

function detalhar(id) {
  router.push(`/loja/vendas/${id}/detalhar`);
}

function editar(id) {
  router.push(`/loja/vendas/${id}`);
}

async function imprimir(id) {
  if (!(await authorizeReceiptAction('emitir_recibo_vendas'))) return;
  router.push({ name: 'ReciboVenda', params: { id } });
}

async function excluir(id) {
  const ok = confirm('Confirma exclusão?');
  if (!ok) return;

  await fetchPermissions();

  try {
    await axios.delete(`/loja/vendas/${id}`);
    await loadComChecagem();
    alert('Venda excluída');
  } catch (e) {
    alert(e.response?.data?.error || 'Erro ao excluir');
  }
}

async function toggleSort(key) {
  page.value = 1;
  await sort(key);
  sortKey.value = key;
  await loadComChecagem();
}

async function loadComChecagem() {
  /*
    O backend já aplica os filtros:
    - quando
    - cliente
    - status

    E também já devolve:
    - data
    - totalItems
    - totalPages

    Portanto, não devemos filtrar status novamente no frontend.
  */
  await loadRawVendas();

  items.value = rawItems.value.map(v => ({
    ...v,
    pendente: v.pendente_calc ?? false
  }));

  displayItems.value = [...items.value];
}

async function filtrar() {
  page.value = 1;
  await loadComChecagem();
}

async function limparFiltros() {
  page.value = 1;

  Object.assign(filters.value, {
    quando: 'Todos',
    cliente: '',
    status: 'Todos'
  });

  await loadComChecagem();
}

async function onPageChange(newPage) {
  page.value = Number(newPage) || 1;
  await loadComChecagem();
}

async function onChangeLimit(newLimit) {
  limit.value = Number(newLimit) || 10;
  page.value = 1;
  await loadComChecagem();
}

onMounted(async () => {
  await Promise.all([fetchPermissions(), fetchReceiptPermissions()]);
  await loadComChecagem();
});
</script>

<style scoped>
.same-height {
  height: 30px;
}

.cursor-pointer {
  cursor: pointer;
}

.cursor-not-allowed {
  cursor: not-allowed;
}

:deep(.pagination.pagination-sm .page-link) {
  line-height: 1;
  font-size: 0.8rem !important;
  border-radius: 0 !important;
}
</style>